// src/components/common/ProjectSteps/ProjectSteps.jsx
import React from 'react';
import { FiCheck } from 'react-icons/fi';
import styles from './ProjectSteps.module.css';

const PROJECT_STEPS = [
    { id: 'formula', label: 'Formula', icon: '🧪' },
    { id: 'manipulation', label: 'Manipulation', icon: '🔬' },
    { id: 'quality', label: 'Quality Control', icon: '✓' },
    { id: 'client', label: 'Send to Client', icon: '📦' },
    { id: 'status', label: 'Sample Status', icon: '🏷️' },
];

const ProjectSteps = ({
    currentStep,
    onStepChange,
    project,
    t,
    stepLabels = {},
    statusLabels = {},
    disabled = false,
}) => {
    const getStepStatus = (stepId) => {
        if (!project) return 'pending';
        const stepData = project.steps?.[stepId];
        if (!stepData) return 'pending';
        return stepData.status || 'pending';
    };

    const isStepComplete = (stepId) => {
        const status = getStepStatus(stepId);
        return status === 'completed' || status === 'approved' || status === 'product_registered';
    };

    const isStepActive = (stepId) => currentStep === stepId;

    const isStepBlocked = (stepId) => {
        if (stepId === 'formula') return false;
        const prevSteps = PROJECT_STEPS.filter(s => {
            const idx = PROJECT_STEPS.findIndex(st => st.id === stepId);
            return PROJECT_STEPS.indexOf(s) < idx;
        });
        return prevSteps.some(s =>
            getStepStatus(s.id) !== 'completed' &&
            getStepStatus(s.id) !== 'approved' &&
            getStepStatus(s.id) !== 'product_registered'
        );
    };

    const getStepLabel = (stepId) => {
        const defaultLabels = {
            'formula': t('pages.development.projectSteps.formula'),
            'manipulation': t('pages.development.projectSteps.manipulation'),
            'quality': t('pages.development.projectSteps.quality'),
            'client': t('pages.development.projectSteps.client'),
            'status': t('pages.development.projectSteps.status')
        };
        return stepLabels[stepId] || defaultLabels[stepId] || stepId;
    };

    const getStatusText = (stepId, status, isActive, isBlocked) => {
        const defaultStatusLabels = {
            inProgress: t('pages.development.projectSteps.inProgress'),
            completed: t('pages.development.projectSteps.completed'),
            blocked: t('pages.development.projectSteps.blocked'),
            pending: t('pages.development.projectSteps.pending'),
        };
        const labels = { ...defaultStatusLabels, ...statusLabels };

        if (isActive) return labels.inProgress;
        if (status === 'completed' || status === 'approved' || status === 'product_registered') {
            return labels.completed;
        }
        if (isBlocked) return labels.blocked;
        return labels.pending;
    };

    // Use the shared step definitions or custom ones
    const steps = PROJECT_STEPS.map(step => ({
        ...step,
        label: getStepLabel(step.id),
    }));

    return (
        <div className={styles.stepsContainer}>
            {steps.map((step, index) => {
                const status = getStepStatus(step.id);
                const isComplete = isStepComplete(step.id);
                const isActive = isStepActive(step.id);
                const isBlocked = isStepBlocked(step.id);
                const isPending = status === 'pending' && !isBlocked;

                let stepClass = styles.stepItem;
                if (isActive) stepClass += ` ${styles.stepActive}`;
                if (isComplete) stepClass += ` ${styles.stepComplete}`;
                if (isPending) stepClass += ` ${styles.stepPending}`;
                if (isBlocked) stepClass += ` ${styles.stepBlocked}`;

                return (
                    <React.Fragment key={step.id}>
                        <div
                            className={stepClass}
                            onClick={() => !isBlocked && !disabled && onStepChange(step.id)}
                            style={{ cursor: isBlocked || disabled ? 'not-allowed' : 'pointer' }}
                        >
                            <div className={styles.stepIconWrapper}>
                                <span className={styles.stepIcon}>{step.icon}</span>
                                {isComplete && <span className={styles.stepCheckmark}><FiCheck size={10} /></span>}
                            </div>
                            <div className={styles.stepContent}>
                                <span className={styles.stepLabel}>{step.label}</span>
                                <span className={styles.stepStatus}>
                                    {getStatusText(step.id, status, isActive, isBlocked)}
                                </span>
                            </div>
                        </div>
                        {index < steps.length - 1 && (
                            <div className={`${styles.stepConnector} ${isComplete ? styles.connectorComplete : ''}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ProjectSteps;